<script setup lang="ts">
import { ROUTES, LANDING_CONTENT } from '~/utils/constants'
const words = [...LANDING_CONTENT.HERO_SECTION.WORDS]
const currentWord = ref<string>(words[0]!)
let interval: NodeJS.Timeout

onMounted(() => {
  let index = 0
  interval = setInterval(() => {
    index = (index + 1) % words.length
    currentWord.value = words[index] as string
  }, 2500)
})

onUnmounted(() => {
  clearInterval(interval)
})
</script>

<template>
  <section class="relative pt-20 pb-16 lg:pt-32 lg:pb-24 overflow-visible">
    <UContainer>
      <div class="text-center max-w-4xl mx-auto relative z-10">
        <!-- Badge -->
        <div
          class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-800/50 mb-8 animate-fade-in-up"
        >
          <span class="flex h-2 w-2 relative">
            <span
              class="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"
            ></span>
            <span class="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
          </span>
          <span class="text-xs font-semibold text-indigo-600 dark:text-indigo-400">{{
            LANDING_CONTENT.HERO_SECTION.BADGE
          }}</span>
        </div>

        <!-- Headline -->
        <h1
          class="text-5xl lg:text-7xl font-bold tracking-tight mb-8 text-gray-900 dark:text-white font-display leading-tight"
        >
          Master French <br class="hidden sm:block" />
          <span
            class="relative inline-block min-w-[200px] text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-purple-600 dark:from-indigo-400 dark:to-purple-400"
          >
            <Transition name="fade-slide" mode="out-in">
              <span :key="currentWord" class="block">{{ currentWord }}</span>
            </Transition>
          </span>
          <span class="text-gray-900 dark:text-white">Naturally</span>
        </h1>

        <!-- Subtext -->
        <p class="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mb-10 leading-relaxed">
          {{ LANDING_CONTENT.HERO_SECTION.SUBTEXT }}
        </p>

        <!-- CTA Buttons -->
        <div class="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
          <UButton
            size="xl"
            :to="ROUTES.SIGNUP"
            color="primary"
            variant="solid"
            class="rounded-xl px-8 py-3 shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/30 transition-all duration-300 transform hover:-translate-y-0.5"
            icon="i-heroicons-rocket-launch"
            trailing
          >
            {{ LANDING_CONTENT.HERO_SECTION.CTA_START }}
          </UButton>
          <UButton
            size="xl"
            variant="ghost"
            color="neutral"
            to="#features"
            class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            {{ LANDING_CONTENT.HERO_SECTION.CTA_EXPLORE }}
          </UButton>
        </div>
      </div>

      <!-- Dashboard Preview / Product Visual -->
      <div class="relative max-w-5xl mx-auto perspective-1000 group">
        <!-- Floating Elements -->
        <div
          class="absolute -left-12 top-1/2 transform -translate-y-1/2 z-20 hidden lg:block animate-float"
        >
          <div
            class="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 w-48 backdrop-blur-sm bg-opacity-90 dark:bg-opacity-90"
          >
            <div class="flex items-center gap-3 mb-2">
              <div
                class="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center text-green-600 dark:text-green-400"
              >
                <UIcon name="i-heroicons-check" class="w-5 h-5" />
              </div>
              <div>
                <div class="text-xs text-gray-500 dark:text-gray-400">Pronunciation</div>
                <div class="text-sm font-bold text-gray-900 dark:text-white">Excellent</div>
              </div>
            </div>
            <div class="w-full bg-gray-100 dark:bg-gray-700 h-1.5 rounded-full overflow-hidden">
              <div class="bg-green-500 h-full w-[92%] rounded-full"></div>
            </div>
          </div>
        </div>

        <div
          class="absolute -right-8 top-20 z-20 hidden lg:block animate-float animation-delay-2000"
        >
          <div
            class="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 w-48 backdrop-blur-sm bg-opacity-90 dark:bg-opacity-90"
          >
            <div class="flex items-center gap-3 mb-2">
              <div
                class="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400 font-bold"
              >
                B2
              </div>
              <div>
                <div class="text-xs text-gray-500 dark:text-gray-400">Target Level</div>
                <div class="text-sm font-bold text-gray-900 dark:text-white">Advanced</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Main Dashboard Mockup -->
        <div
          class="relative rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 shadow-2xl overflow-hidden transform rotate-x-2 group-hover:rotate-x-0 transition-transform duration-700 ease-out"
        >
          <div
            class="flex items-center gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800/50"
          >
            <div class="flex gap-1.5">
              <div class="w-3 h-3 rounded-full bg-red-400"></div>
              <div class="w-3 h-3 rounded-full bg-yellow-400"></div>
              <div class="w-3 h-3 rounded-full bg-green-400"></div>
            </div>
            <div class="ml-4 flex-1">
              <div
                class="mx-auto w-1/2 h-6 bg-gray-100 dark:bg-gray-700/50 rounded-lg text-xs flex items-center justify-center text-gray-400 font-mono"
              >
                comepass.app/dashboard
              </div>
            </div>
          </div>

          <!-- Mock App Content -->
          <div class="grid grid-cols-12 h-[400px] lg:h-[500px]">
            <!-- Sidebar -->
            <div
              class="col-span-2 border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 hidden md:block"
            >
              <div class="space-y-4">
                <div class="h-8 w-8 rounded-lg bg-indigo-600 mb-8"></div>
                <div
                  v-for="i in 5"
                  :key="i"
                  class="h-8 w-full rounded-lg bg-gray-100 dark:bg-gray-800/50"
                ></div>
              </div>
            </div>
            <!-- Main Area -->
            <div class="col-span-12 md:col-span-10 bg-gray-50 dark:bg-gray-950 p-6 sm:p-8">
              <div class="flex justify-between items-center mb-8">
                <div>
                  <div class="h-6 w-32 bg-gray-200 dark:bg-gray-800 rounded mb-2"></div>
                  <div class="h-4 w-48 bg-gray-100 dark:bg-gray-800/50 rounded"></div>
                </div>
                <div class="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-800"></div>
              </div>

              <div class="grid grid-cols-3 gap-6 mb-8">
                <div
                  v-for="i in 3"
                  :key="i"
                  class="h-32 rounded-xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 p-4 shadow-sm"
                >
                  <div class="h-10 w-10 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 mb-3"></div>
                  <div class="h-4 w-1/2 bg-gray-100 dark:bg-gray-800 rounded"></div>
                </div>
              </div>

              <div
                class="h-64 rounded-xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 p-6 shadow-sm flex items-center justify-center"
              >
                <div class="text-center">
                  <div
                    class="h-20 w-20 mx-auto rounded-full bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center mb-4"
                  >
                    <UIcon name="i-heroicons-microphone" class="w-10 h-10 text-indigo-500" />
                  </div>
                  <div class="h-4 w-64 mx-auto bg-gray-100 dark:bg-gray-800 rounded mb-2"></div>
                  <div class="h-4 w-32 mx-auto bg-gray-100 dark:bg-gray-800 rounded"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Glass overlay for depth -->
          <div
            class="absolute inset-0 bg-gradient-to-t from-white/20 to-transparent pointer-events-none"
          ></div>
        </div>
      </div>
    </UContainer>
  </section>
</template>

<style scoped>
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.5s ease;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.perspective-1000 {
  perspective: 1000px;
}

.rotate-x-2 {
  transform: rotateX(2deg);
}

.animate-float {
  animation: float 6s ease-in-out infinite;
}

.animation-delay-2000 {
  animation-delay: 2s;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-15px);
  }
}
</style>
